import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, FormRecord, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { siteLocale } from '@models/site-locales';
import type { WaterMarketFee } from '@models/water-market-fees';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmWaterMarketFeesService } from '@services/crm-water-market-fees.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { finalize, type Observable, tap } from 'rxjs';
import { MAX_PRICE, priceMargin } from '../../shared/pricing-grid/pricing-grid.model';
import type { MarketFeeForm, MarketFeeRow } from './water-market-fees.model';
import { dogPairValidator } from './water-market-fees.validators';

/**
 * Lake-level per-market fee editor (CS-287): one row per market — a site — holding the dog fee
 * (purchase + sale per dog) and the flat booking/service fee (0 = disabled). Both are flat, i.e. not
 * season-dependent, so unlike the pricing grids there is no price list and no season axis. Three loose
 * numbers per row is a form, not a price matrix, so it stays a form: the shared grid store pairs a
 * purchase with a sale, and the booking fee has nothing to pair with.
 *
 * The rows are whatever the API returns: it answers with every site, in site order, filling markets that
 * have no stored fee with defaults. Nothing about the market vocabulary is held here.
 *
 * This is a section of the water form, not a screen of its own: the page's Save persists the water and
 * then folds in these fees, the same way it folds in the exclusive pricing grid. The parent drives that
 * through {@link dirty}, {@link validate}, {@link save} and {@link reload}, and owns every save toast.
 */
@Component({
  selector: 'app-water-market-fees',
  templateUrl: './water-market-fees.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
})
export class WaterMarketFeesComponent implements OnInit {
  /** Owning water id. The section only renders for a saved water, so this is always a real id. */
  readonly waterId = input.required<number>();

  private readonly marketFeesService = inject(CrmWaterMarketFeesService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  /** The markets to render, in the order the API returned them. */
  readonly rows = signal<MarketFeeRow[]>([]);

  readonly isLoading = signal(false);

  /**
   * The section has nothing to show because a load failed, as opposed to because there is nothing to show.
   * Held apart from an empty {@link rows} so the two can say different things: the API answers with every
   * market, so an empty response really does mean no markets exist, while a failed GET means nothing at all.
   */
  readonly loadFailed = signal(false);

  /** One group per site, keyed by site id. Built from the load; the API decides which markets exist. */
  readonly form = new FormRecord<FormGroup<MarketFeeForm>>({});

  /**
   * Reveal the inline errors. Held rather than keyed off `touched` so that errors appear when Save is
   * attempted, not the moment a field is blurred — matching both CS-286 pricing sections.
   */
  readonly showErrors = signal(false);

  /**
   * Discards responses from superseded loads. A reload fired while the first GET is still in flight would
   * otherwise let the slower response land last and overwrite what the user has since typed.
   */
  private loadToken = 0;

  ngOnInit(): void {
    this.load();
  }

  /**
   * Re-read the fees after the page has saved. The markets do not depend on the water, so this only
   * matters for adopting what the API stored — but it keeps the section in step with the exclusive grid,
   * which the page reloads for the same reason.
   */
  reload(): void {
    this.load();
  }

  /** Bound to the retry button the failed state offers; nothing else re-attempts a load on its own. */
  protected retryLoad(): void {
    this.load();
  }

  private load(): void {
    const token = ++this.loadToken;
    this.isLoading.set(true);
    this.marketFeesService
      .getFees(this.waterId())
      .pipe(
        // `null`, not an empty set: a failure is the absence of an answer, and adopting `[]` for it would
        // both throw away the rows on screen and have the section claim the lake has no markets.
        showGenericErrorToast(this.toastService, () => null, this.translate.instant('waters.market-fees.load-failed')),
        finalize(() => {
          if (token === this.loadToken) this.isLoading.set(false);
        }),
      )
      .subscribe((fees) => {
        if (token !== this.loadToken) return;
        if (fees === null) {
          // Leave the rows the user was looking at alone. A failed post-save reload costs nothing: `save()`
          // has already adopted the PUT response, which is the same document this GET returns.
          this.loadFailed.set(this.rows().length === 0);
          return;
        }
        this.loadFailed.set(false);
        this.adopt(fees);
      });
  }

  /**
   * Take a GET or PUT response as the new truth: rows, labels and values all come from it, and the form
   * comes out pristine — the parent's dirty gate must not see a load as an edit.
   */
  private adopt(fees: WaterMarketFee[]): void {
    const rows: MarketFeeRow[] = [];

    for (const fee of fees) {
      const key = String(fee.siteId);
      if (!this.form.contains(key)) this.form.addControl(key, this.newRow());
      const group = this.form.controls[key];
      group.patchValue({
        dogPurchasePrice: fee.dogPurchasePrice,
        dogSellingPrice: fee.dogSellingPrice,
        bookingFee: fee.bookingFee,
      });
      const locale = siteLocale(fee.locale);
      rows.push({
        siteId: fee.siteId,
        country: locale?.country ?? fee.locale,
        flag: locale?.flag ?? null,
        group,
      });
    }

    // A market the API no longer returns has no row to edit; drop its control so it cannot be sent.
    const returned = new Set(fees.map((f) => String(f.siteId)));
    for (const key of Object.keys(this.form.controls)) {
      if (!returned.has(key)) this.form.removeControl(key);
    }

    this.rows.set(rows);
    this.form.markAsPristine();
    this.showErrors.set(false);
  }

  /**
   * Margin on the row's dog fee, as a percentage of its sale price — the same figure, from the same
   * helper, that the season pricing grids show under each of their cells. Null until both prices are in.
   */
  margin(row: MarketFeeRow): number | null {
    return priceMargin(row.group.controls.dogPurchasePrice.value, row.group.controls.dogSellingPrice.value);
  }

  // --- Validation state for the template ---

  /** Any market with exactly one of its two dog prices filled in. */
  hasPartialRows(): boolean {
    return this.rows().some((row) => row.group.hasError('dogPair'));
  }

  /** Any amount outside the range the API accepts. */
  hasOutOfRangeRows(): boolean {
    return this.rows().some((row) => Object.values(row.group.controls).some((c) => this.isOutOfRange(c)));
  }

  /** True when either dog price is out of range — the pair shares one note line, so it shares the check. */
  isDogOutOfRange(row: MarketFeeRow): boolean {
    return (
      this.isOutOfRange(row.group.controls.dogPurchasePrice) || this.isOutOfRange(row.group.controls.dogSellingPrice)
    );
  }

  // --- Submitted by the page's form (see WaterDetailsComponent.save) ---

  /** Whether anything has been edited since the last load or save. */
  dirty(): boolean {
    return this.form.dirty;
  }

  /**
   * Whether the table is fit to send, revealing the inline errors when it isn't: a dog fee needs both
   * prices or neither, and no amount may fall outside the range the API accepts. Checked before the water
   * itself is saved, so a half-filled fee costs nothing.
   */
  validate(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showErrors.set(true);
      return false;
    }
    this.showErrors.set(false);
    return true;
  }

  /**
   * Build the PUT for the whole set, or `null` when there is nothing to send. The parent chains this into
   * its own submit and reports the outcome; the response is folded back in here so anything the API
   * changed — rows dropped for carrying no fee, amounts rounded to two decimals — is what the user ends
   * up looking at, and the section comes out of the save pristine.
   *
   * Nothing is sent while a load is in flight, or before the first load has landed: the payload would be
   * built from rows that are not the ones the user is looking at.
   */
  save(): Observable<WaterMarketFee[]> | null {
    if (this.isLoading() || this.rows().length === 0) return null;

    const fees: WaterMarketFee[] = this.rows().map(({ siteId, group }) => ({
      siteId,
      locale: '', // response-only; the API keys on siteId and ignores this
      dogPurchasePrice: group.controls.dogPurchasePrice.value,
      dogSellingPrice: group.controls.dogSellingPrice.value,
      bookingFee: group.controls.bookingFee.value ?? 0,
    }));

    return this.marketFeesService.saveFees(this.waterId(), { fees }).pipe(tap((saved) => this.adopt(saved)));
  }

  // --- Form plumbing ---

  /** Whether a control holds an amount outside the range the API accepts. */
  private isOutOfRange(control: FormControl<number | null>): boolean {
    return control.hasError('min') || control.hasError('max');
  }

  /** A blank market row. Bounds match the API's `[Range(0, 99999999.99)]` on every amount. */
  private newRow(): FormGroup<MarketFeeForm> {
    const price = () => new FormControl<number | null>(null, [Validators.min(0), Validators.max(MAX_PRICE)]);
    return new FormGroup<MarketFeeForm>(
      { dogPurchasePrice: price(), dogSellingPrice: price(), bookingFee: price() },
      { validators: dogPairValidator },
    );
  }
}
